import axiosInstance from '@/plugins/axios';

// O parser (agnes-compliance-parser, Java) é um SERVICE separado do BFF, com host
// e context-path (`esocial-service`) próprios. NÃO é o `VITE_APP_API` (BFF), senão
// o request cai no BFF e volta 404. Aponta pro parser via `VITE_APP_PARSER_API`.
// Fallback = VITE_APP_API só pra não quebrar build em env sem a var configurada.
const PARSER_BASE_URL = import.meta.env.VITE_APP_PARSER_API || import.meta.env.VITE_APP_API;

export default class ServiceESocial {
  static endpoint = 'esocial';

  static eSocialUpload = {
    // Listagem vem do BFF (agnes-compliance-api), não do parser: a query de
    // listagem do parser é quebrada (filtra por uma coluna de CNPJ nunca
    // preenchida) e nunca mostra o import, mesmo após o processamento terminar
    // com sucesso (CON-5160). O BFF lê o mesmo banco do parser direto e escopa
    // certo por CNPJ via file_process_detail.
    getAll: (page = 0, size = 50, q, sort, status, nrInsc) => {
      return axiosInstance.get('esocial-importacao', {
        params: { page, size, q, ...(sort ? { sort } : {}), status, nrInsc },
      });
    },
    // Contagem por status para os cards da tela (CON-4721), no mesmo escopo/filtros
    // da listagem (busca + empregador). O clique nos cards filtra a tabela por status.
    getTotalizadores: (q, nrInsc) => {
      return axiosInstance.get('esocial-importacao/totalizadores', {
        params: { q, nrInsc },
      });
    },
    upload: (formData, onUploadProgress) => {
      return axiosInstance.post('esocial-service/file-process/upload', formData, {
        baseURL: PARSER_BASE_URL,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress,
      });
    },
    // Excluir uma importação COM FALHA (CON-5268) — vai pro BFF (não pro parser), que
    // valida o status de erro e audita. Libera o re-upload do mesmo zip.
    delete: (id) => axiosInstance.delete(`esocial-importacao/${id}`),
  };

  static evtadmissao = {
    search: (params) => {
      return axiosInstance.get(`${this.endpoint}/evtadmissao/search`, {
        params,
      });
    },
  };

  static evtdeslig = {
    search: (params) => {
      return axiosInstance.get(`${this.endpoint}/evtdeslig/search`, {
        params,
      });
    },
  };

  static evttabrubrica = {
    get: (id) => {
      return axiosInstance.get(`${this.endpoint}/evttabrubrica/${id}`);
    },
    search: (pagination, params) => {
      return axiosInstance.post(`${this.endpoint}/evttabrubrica/filter?${pagination}`, params);
    },
    searchHistoryEsocial: (pagination, params) => {
      return axiosInstance.get(
        `${this.endpoint}/evttabrubrica/search-history-esocial?${pagination}`,
        {
          params,
        },
      );
    },
    save: (params) => {
      return axiosInstance.post(`${this.endpoint}/evttabrubrica/save-new-rubrica`, params);
    },
    getProcessCP: () => {
      return axiosInstance.get(`${this.endpoint}/evttabrubrica-processo-cp`);
    },
    getProcessIRRF: () => {
      return axiosInstance.get(`${this.endpoint}/evttabrubrica-processo-irrf`);
    },
    getProcessFGTS: () => {
      return axiosInstance.get(`${this.endpoint}/evttabrubrica-processo-fgts`);
    },
  };

  static rubricahistorico = {
    search: (id) => {
      return axiosInstance.get(`${this.endpoint}/rubricahistorico/evtrubrica/${id}`);
    },
    searchRubricaHistory: (params) => {
      return axiosInstance.get(`${this.endpoint}/rubricahistorico/evtrubrica?${params}`);
    },
    filters: () => {
      return axiosInstance.get(`${this.endpoint}/evttabrubrica/get-filters`);
    },
    save: (itemToSave) => {
      return axiosInstance.post(`${this.endpoint}/rubricahistorico/save`, itemToSave);
    },
    update: (itemToSave) => {
      return axiosInstance.put(
        `${this.endpoint}/rubricahistorico/${itemToSave.rubricaHistorico.id}`,
        itemToSave,
      );
    },
    delete: (id) => {
      return axiosInstance.delete(`${this.endpoint}/rubricahistorico/${id}`);
    },
    download: (filter) => {
      return axiosInstance.post(`${this.endpoint}/rubricahistorico/download-xls`, filter, {
        responseType: 'arraybuffer',
        contentType: 'application/force-download',
        'Content-Disposition': 'attachment',
      });
    },
  };

  static establishment = {
    getByUserId: () => {
      return axiosInstance.get(`${this.endpoint}/establishment-company/userid`);
    },
  };

  static report = {
    generate: (nrinscrideempregador) => {
      return axiosInstance.get(
        `${this.endpoint}/evtremuns/generate-report/${nrinscrideempregador}`,
      );
    },
    getAll: (sort = 'startProcess,desc') => {
      // CON-5204: ordenação server-side; default = data de solicitação desc (mais recente no topo).
      return axiosInstance.get(`report-process?sort=${sort}`);
    },
    download: (id) => {
      return axiosInstance.get(`report-process/${id}/download`, { responseType: 'blob' });
    },
  };

  static collaborators = {
    details: (idTrabalhador) => {
      return axiosInstance.get(`${this.endpoint}/collaborator/${idTrabalhador}/details`);
    },
    eventsHorariosJornada: (idTrabalhador, pagination, filter) => {
      return axiosInstance.post(
        `${this.endpoint}/collaborator/${idTrabalhador}/horarios-jornada/events?${pagination}`,
        filter,
      );
    },
  };

  static rubricAnalysis = {
    analysis: (params, pagination) => {
      return axiosInstance.post(
        `${this.endpoint}/rubric-automation/analysis?${pagination}`,
        params,
      );
    },
    analysisById: (rubricId) => {
      return axiosInstance.post(`${this.endpoint}/rubric-automation/analysis/${rubricId}`, {});
    },
    salvarAnalise: (params, idRubrica) => {
      return axiosInstance.put(`${this.endpoint}/rubric-automation/analysis/${idRubrica}`, params);
    },
    analysisException: (rubricId) => {
      return axiosInstance.get(`${this.endpoint}/rubric-automation/analysis/${rubricId}/exception`);
    },
    analysisHistory: (rubricId, pagination, params) => {
      return axiosInstance.post(
        `${this.endpoint}/rubric-automation/analysis/${rubricId}/history?${pagination}`,
        params,
      );
    },
    analysisHistoryFilter: (rubricId) => {
      return axiosInstance.get(
        `${this.endpoint}/rubric-automation/analysis/${rubricId}/history/filters`,
      );
    },
    analysisHistoryExport: (rubricId, params) => {
      return axiosInstance.post(
        `${this.endpoint}/rubric-automation/analysis/${rubricId}/history/export`,
        params,
      );
    },
    createException: (rubricId, params) => {
      return axiosInstance.post(
        `${this.endpoint}/rubric-automation/analysis/${rubricId}/exception`,
        params,
      );
    },
    editException: (rubricId, params) => {
      return axiosInstance.put(
        `${this.endpoint}/rubric-automation/analysis/${rubricId}/exception/${params.id}`,
        params,
      );
    },
    deleteException: (rubricId, exceptionId) => {
      return axiosInstance.delete(
        `${this.endpoint}/rubric-automation/analysis/${rubricId}/exception/${exceptionId}`,
      );
    },
    historicException: (rubricId, exceptionId) => {
      return axiosInstance.get(
        `${this.endpoint}/rubric-automation/analysis/${rubricId}/exception/${exceptionId}/history`,
      );
    },
    saveWontApply: (rubricId, params) => {
      return axiosInstance.put(
        `${this.endpoint}/rubric-automation/analysis/${rubricId}/wont-apply`,
        params,
      );
    },
    editWontApply: (idRubrica, params) => {
      return axiosInstance.put(
        `${this.endpoint}/rubric-automation/analysis/${idRubrica}/wont-apply`,
        params,
      );
    },
    deleteWontApply: (rubricId) => {
      return axiosInstance.delete(
        `${this.endpoint}/rubric-automation/analysis/${rubricId}/wont-apply`,
      );
    },
    export: (params) => {
      return axiosInstance.post(`${this.endpoint}/rubric-automation/analysis/export`, params);
    },
    exportClientViewList: (params) => {
      return axiosInstance.post(
        `${this.endpoint}/rubric-automation/analysis/export/client-view`,
        params,
      );
    },
    graphicGeneralVision: (params) => {
      return axiosInstance.post(
        `${this.endpoint}/rubric-automation/analysis/count/general-vision`,
        params,
      );
    },
    analysisCountPendingExplanation: (params) => {
      return axiosInstance.post(
        `${this.endpoint}/rubric-automation/analysis/count/pending-explanation`,
        params,
      );
    },
    filterOptions: () => {
      return axiosInstance.get(`${this.endpoint}/rubric-automation/analysis/filters`);
    },
    getRubricGraficosClassificacaoAnalise: (filters) => {
      return axiosInstance.post(
        `${this.endpoint}/rubric-automation/analysis/classification/graph`,
        filters,
      );
    },
    getRubricGraficoEtapaAnalise: (filters) => {
      return axiosInstance.post(`${this.endpoint}/rubric-analysis/stages/graph`, filters);
    },
    getRubricGraficoAnaliseAutomatica: (filters) => {
      return axiosInstance.post(
        `${this.endpoint}/rubric-automation/analysis/count/automatic-analysis`,
        filters,
      );
    },
    getGraficosAplicacaoRecomendacao: (filters) => {
      return axiosInstance.post(
        `${this.endpoint}/rubric-automation/analysis/recommendation-application/graph`,
        filters,
      );
    },
    getGraficoRisco: (filters) => {
      return axiosInstance.post(`${this.endpoint}/rubric-automation/analysis/risk-graph`, filters);
    },
    getGraficoSituacaoRubrica: (filters) => {
      return axiosInstance.post(
        `${this.endpoint}/rubric-automation/analysis/situation-graph`,
        filters,
      );
    },
  };

  static rubricAutomationConfig = {
    rubricNatureList: (params, pagination) => {
      return axiosInstance.post(
        `${this.endpoint}/rubric-automation/config/nature?${pagination}`,
        params,
      );
    },
    rubricNatureCreate: (rubricNature) => {
      return axiosInstance.post(
        `${this.endpoint}/rubric-automation/config/nature/create`,
        rubricNature,
      );
    },
    rubricNatureUpdate: (rubricNature) => {
      return axiosInstance.put(
        `${this.endpoint}/rubric-automation/config/nature/update`,
        rubricNature,
      );
    },
    natureTypesSave: (natureType) => {
      return axiosInstance.post(
        `${this.endpoint}/rubric-automation/config/type/create`,
        natureType,
      );
    },
    natureTypesUpdate: (natureType) => {
      return axiosInstance.put(`${this.endpoint}/rubric-automation/config/type/update`, natureType);
    },
    incidenceINSSSave: (incidenceINSS) => {
      return axiosInstance.post(
        `${this.endpoint}/rubric-automation/config/inss-incidence/create`,
        incidenceINSS,
      );
    },
    incidenceINSSUpdate: (incidenceINSS) => {
      return axiosInstance.put(
        `${this.endpoint}/rubric-automation/config/inss-incidence/update`,
        incidenceINSS,
      );
    },
    incidenceINSSHistory: (code) => {
      return axiosInstance.get(
        `${this.endpoint}/rubric-automation/config/inss-incidence/${code}/history`,
        code,
      );
    },
    incidenceIRRFSave: (incidenceIRRF) => {
      return axiosInstance.post(
        `${this.endpoint}/rubric-automation/config/irrf-incidence/create`,
        incidenceIRRF,
      );
    },
    incidenceIRRFUpdate: (incidenceIRRF) => {
      return axiosInstance.put(
        `${this.endpoint}/rubric-automation/config/irrf-incidence/update`,
        incidenceIRRF,
      );
    },
    incidenceIRRFHistory: (code) => {
      return axiosInstance.get(
        `${this.endpoint}/rubric-automation/config/irrf-incidence/${code}/history`,
      );
    },
    incidenceFGTSSave: (incidenceFGTS) => {
      return axiosInstance.post(
        `${this.endpoint}/rubric-automation/config/fgts-incidence/create`,
        incidenceFGTS,
      );
    },
    incidenceFGTSUpdate: (incidenceFGTS) => {
      return axiosInstance.put(
        `${this.endpoint}/rubric-automation/config/fgts-incidence/update`,
        incidenceFGTS,
      );
    },
    incidenceFGTSHistory: (code) => {
      return axiosInstance.get(
        `${this.endpoint}/rubric-automation/config/fgts-incidence/${code}/history`,
      );
    },
    incidenceCPRPSave: (incidenceCPRP) => {
      return axiosInstance.post(
        `${this.endpoint}/rubric-automation/config/cprp-incidence/create`,
        incidenceCPRP,
      );
    },
    incidenceCPRPUpdate: (incidenceCPRP) => {
      return axiosInstance.put(
        `${this.endpoint}/rubric-automation/config/cprp-incidence/update`,
        incidenceCPRP,
      );
    },
    incidenceNatureList: (params, pagination) => {
      return axiosInstance.post(
        `${this.endpoint}/rubric-automation/config/incidence-by-nature?${pagination}`,
        params,
      );
    },
    incidenceNatureSave: (incidenceNature) => {
      return axiosInstance.put(
        `${this.endpoint}/rubric-automation/config/incidence-by-nature`,
        incidenceNature,
      );
    },
    incidenceNatureExport: (params) => {
      return axiosInstance.post(
        `${this.endpoint}/rubric-automation/config/incidence-by-nature/export`,
        params,
      );
    },
    incidencePISPASEPSave: (params) => {
      return axiosInstance.post(
        `${this.endpoint}/rubric-automation/config/pispasep-incidence/create`,
        params,
      );
    },
    incidencePISPASEPUpdate: (params) => {
      return axiosInstance.put(
        `${this.endpoint}/rubric-automation/config/pispasep-incidence/update`,
        params,
      );
    },
    incidencePISPASEPHistory: (code) => {
      return axiosInstance.get(
        `${this.endpoint}/rubric-automation/config/pispasep-incidence/${code}/history`,
      );
    },
    incidencePISPASEPFilterOptions: (params) => {
      return axiosInstance.get(
        `${this.endpoint}/rubric-automation/config/pispasep-incidence/filter-options`,
        params,
      );
    },
    listaClassificacaoAnalise: (params, pagination) => {
      return axiosInstance.post(
        `${this.endpoint}/rubric-automation/config/analysis-classification?${pagination}`,
        params,
      );
    },
    filtrosClassificacaoAnalise: () => {
      return axiosInstance.get(
        `${this.endpoint}/rubric-automation/config/analysis-classification/filters`,
      );
    },
    salvaClassificacaoAnalise: (classificacaoAnalise) => {
      return axiosInstance.put(
        `${this.endpoint}/rubric-automation/config/analysis-classification`,
        classificacaoAnalise,
      );
    },
    historicoClassificacaoAnalise: (code) => {
      return axiosInstance.get(
        `${this.endpoint}/rubric-automation/config/analysis-classification/${code}/history`,
      );
    },
    incidenceIRRFFilters: () => {
      return axiosInstance.get(`${this.endpoint}/rubric-automation/config/irrf-incidence/filters`);
    },
    incidenceINSSFilters: () => {
      return axiosInstance.get(`${this.endpoint}/rubric-automation/config/inss-incidence/filters`);
    },
    rubricNatureFilters: () => {
      return axiosInstance.get(`${this.endpoint}/rubric-automation/config/nature/filters`);
    },
    incidenceNatureFilters: () => {
      return axiosInstance.get(
        `${this.endpoint}/rubric-automation/config/incidence-by-nature/filters`,
      );
    },
    incidenceNatureHistory: (id) => {
      return axiosInstance.get(
        `${this.endpoint}/rubric-automation/config/incidence-by-nature/${id}/history`,
      );
    },
    history: (tableName, params) => {
      return axiosInstance.post(
        `${this.endpoint}/rubric-automation/config/history/${tableName}`,
        params,
      );
    },
    incidenceCPRPHistory: (code) => {
      return axiosInstance.get(
        `${this.endpoint}/rubric-automation/config/cprp-incidence/${code}/history`,
      );
    },
    inicidenceRubricNatureHistory: (code) => {
      return axiosInstance.get(`${this.endpoint}/rubric-automation/config/nature/${code}/history`);
    },
    incidenceRubricTypeHistory: (code) => {
      return axiosInstance.get(`${this.endpoint}/rubric-automation/config/type/${code}/history`);
    },
  };

  static rubricAutomation = {
    listaMovimentacao: (filters, pagination, rubricAnalysisId) => {
      return axiosInstance.post(
        `${this.endpoint}/rubric-automation/analysis/${rubricAnalysisId}/rubric-event/list?${pagination}`,
        filters,
      );
    },
    exportMovimentacao: (filters, rubricAnalysisId) => {
      return axiosInstance.post(
        `${this.endpoint}/rubric-automation/analysis/${rubricAnalysisId}/rubric-event/export`,
        filters,
      );
    },
    filtersMovimentacao: (rubricAnalysisId) => {
      return axiosInstance.get(
        `${this.endpoint}/rubric-automation/analysis/${rubricAnalysisId}/event/filter`,
      );
    },
  };

  static afastadosEInativos = {
    getDashboard: (value) => {
      return axiosInstance.post(
        `${this.endpoint}/collaborator/dashboard/active-and-retired`,
        value,
      );
    },
    getFilters: () => {
      return axiosInstance.post(`${this.endpoint}/collaborator/dashboard/filters`);
    },
    getTable: (value, pagination) => {
      return axiosInstance.post(
        `${this.endpoint}/collaborator/history/active-and-retired?${pagination}`,
        value,
      );
    },
    export: (params) => {
      return axiosInstance.post(
        `${this.endpoint}/collaborator/history/active-and-retired/export`,
        params,
      );
    },
  };

  static cedidos = {
    getDashboard: (filter) => {
      return axiosInstance.post(`${this.endpoint}/collaborator/dashboard/cessions`, filter);
    },
    getFilters: () => {
      return axiosInstance.post(`${this.endpoint}/collaborator/dashboard/filters`);
    },
    getTable: (value, pagination) => {
      return axiosInstance.post(
        `${this.endpoint}/collaborator/history/cessions?${pagination}`,
        value,
      );
    },
    getCount: (filter) => {
      return axiosInstance.post(`${this.endpoint}/collaborator/dashboard/cessions/count`, filter);
    },
    export: (filter) => {
      return axiosInstance.post(`${this.endpoint}/collaborator/history/cessions/export`, filter, {
        responseType: 'blob',
      });
    },
  };

  static cboOverview = {
    getCboList: (value, pagination) => {
      return axiosInstance.post(
        `${this.endpoint}/collaborator/history/cbo-analysis?${pagination}`,
        value,
      );
    },
    exportCboList: (filter) => {
      return axiosInstance.post(
        `${this.endpoint}/collaborator/history/cbo-analysis/export`,
        filter,
        { responseType: 'blob' },
      );
    },
    exportClientViewCboList: (filter) => {
      return axiosInstance.post(
        `${this.endpoint}/collaborator/history/cbo-analysis/export/client-view`,
        filter,
        { responseType: 'blob' },
      );
    },
    getCboGraficoClassificacaoAnalise: (filter) => {
      return axiosInstance.post(
        `${this.endpoint}/collaborator/history/cbo-analysis/dashboard/classification`,
        filter,
      );
    },
    getCboGraficoEtapasAnalise: (filter) => {
      return axiosInstance.post(
        `${this.endpoint}/collaborator/history/cbo-analysis/dashboard/step`,
        filter,
      );
    },
    getCboGraficoStatusAnalise: (filter) => {
      return axiosInstance.post(
        `${this.endpoint}/collaborator/history/cbo-analysis/dashboard/status`,
        filter,
      );
    },
    dashboardRisk: (filter) => {
      return axiosInstance.post(
        `${this.endpoint}/collaborator/history/cbo-analysis/dashboard/risk`,
        filter,
      );
    },
  };

  static CBO = {
    post: (pagination, data) =>
      axiosInstance.post(`${this.endpoint}/collaborator/history/cbo-analysis?${pagination}`, data),

    analysis: {
      get: (collaboratorId) =>
        axiosInstance.get(`${this.endpoint}/collaborator/${collaboratorId}/cbo/analysis`),
      post: (collaboratorId, data) =>
        axiosInstance.post(`${this.endpoint}/collaborator/${collaboratorId}/cbo/analysis`, data),

      occupations: {
        post: (pagination, data) =>
          axiosInstance.post(`${this.endpoint}/cbo/list?${pagination}`, data),
      },

      occurrences: {
        post: (pagination, data) =>
          axiosInstance.post(
            `${this.endpoint}/cbo-analysis/config/risk-classification/list?${pagination}`,
            data,
          ),
      },

      filters: {
        get: () =>
          axiosInstance.get(`${this.endpoint}/cbo-analysis/config/risk-classification/filters`),
      },

      history: {
        post: (collaboratorId, analysisId, pagination, data) =>
          axiosInstance.post(
            `${this.endpoint}/collaborator/${collaboratorId}/cbo/analysis/${analysisId}/history?${pagination}`,
            data,
          ),

        filter: {
          get: (collaboratorId, analysisId) =>
            axiosInstance.post(
              `${this.endpoint}/collaborator/${collaboratorId}/cbo/analysis/${analysisId}/history/filters`,
            ),
        },
      },
    },
  };

  static cboClassificacaoRisco = {
    buscarLista: (classificacao, paginacao) => {
      return axiosInstance.post(
        `${this.endpoint}/cbo-analysis/config/risk-classification/list?${paginacao}`,
        classificacao,
      );
    },
    buscarUm: (id) => {
      return axiosInstance.get(`${this.endpoint}/cbo-analysis/config/risk-classification/${id}`);
    },
    criar: (classificacao) => {
      return axiosInstance.post(
        `${this.endpoint}/cbo-analysis/config/risk-classification`,
        classificacao,
      );
    },
    editar: (classificacao) => {
      return axiosInstance.put(
        `${this.endpoint}/cbo-analysis/config/risk-classification/${classificacao.id}`,
        classificacao,
      );
    },
    buscarListaHistorico: (id) => {
      return axiosInstance.get(
        `${this.endpoint}/cbo-analysis/config/risk-classification/${id}/history`,
      );
    },
  };

  static cboSearch = {
    getSearchResults: (value, pagination) => {
      return axiosInstance.post(`${this.endpoint}/collaborator/cbo?${pagination}`, value);
    },
    exportCboSearch: (data) => {
      return axiosInstance.post(`${this.endpoint}/collaborator/cbo/export`, data, {
        responseType: 'blob',
      });
    },
  };

  static cboDetails = {
    getCboMovimentacaoXmlList: (filter, pagination, trabalhadorId) => {
      return axiosInstance.post(
        `${this.endpoint}/collaborator/${trabalhadorId}/cbo/events?${pagination}`,
        filter,
      );
    },
    getCboMovimentacaoXmlFilters: (trabalhadorId) => {
      return axiosInstance.get(`${this.endpoint}/collaborator/${trabalhadorId}/cbo/events/filters`);
    },
    exportMovimentacoes: (trabalhadorId, filter) => {
      return axiosInstance.post(
        `${this.endpoint}/collaborator/${trabalhadorId}/cbo/events/export`,
        filter,
      );
    },
  };

  static cboClassification = {
    getCboList: (filter, pagination) => {
      return axiosInstance.post(`${this.endpoint}/cbo/list?${pagination}`, filter);
    },
    exportCboList: (filter) => {
      return axiosInstance.post(`${this.endpoint}/cbo/export`, filter);
    },
    getCboFilters: () => {
      return axiosInstance.post(`${this.endpoint}/cbo/dashboard/filters`);
    },
    searchCboByParams: (filter, pagination) => {
      return axiosInstance.post(
        `${this.endpoint}/cboInfoes/search/findByFamilyID?${pagination}`,
        filter,
      );
    },
  };

  static workSchedules = {
    fetchAll: (pagination, data) =>
      axiosInstance.post(`${this.endpoint}/trabalhador-jornada/?${pagination}`, data),

    filters: {
      fetchAll: () => axiosInstance.get(`${this.endpoint}/trabalhador-jornada/filtros`),
    },

    classification: {
      fetchAll: (pagination, data) =>
        axiosInstance.post(`${this.endpoint}/work-schedules/recommendations?${pagination}`, data),
      create: (data) =>
        axiosInstance.post(`${this.endpoint}/work-schedules/recommendations/create`, data),
      update: (id, data) =>
        axiosInstance.put(`${this.endpoint}/work-schedules/recommendations/update/${id}`, data),
      fetchFilters: () =>
        axiosInstance.get(`${this.endpoint}/work-schedules/recommendations/filters`),
      occurrence: {
        validate: (data) =>
          axiosInstance.post(`${this.endpoint}/work-schedules/recommendations/exists`, data),
      },
    },

    analysisOptions: {
      workRegime: () => {
        return axiosInstance.get(
          `${this.endpoint}/trabalhador-jornada/analysis/work-regime/options`,
        );
      },
      journeyType: () => {
        return axiosInstance.get(
          `${this.endpoint}/trabalhador-jornada/analysis/journey-type/options`,
        );
      },
      partTime: () => {
        return axiosInstance.get(`${this.endpoint}/trabalhador-jornada/analysis/part-time/options`);
      },

      getHorarioEJornadasHistory: (id, filter, pagination) => {
        return axiosInstance.post(
          `${this.endpoint}/trabalhador-jornada/analysis/${id}/history?${pagination}`,
          filter,
        );
      },

      getHorariosEJornadaHistoryFilter: (id, filter) => {
        return axiosInstance.get(
          `${this.endpoint}/trabalhador-jornada/analysis/${id}/history/filter`,
          filter,
        );
      },
    },
    /**
     * @deprecated use 'fetchAll' instead
     */
    getHorarioEJornadasList: (filter, pagination) => {
      return axiosInstance.post(`${this.endpoint}/trabalhador-jornada?${pagination}`, filter);
    },
    salvarHorariosJornadasAnalise: (isAnalise, payload) => {
      return axiosInstance.put(
        `${this.endpoint}/trabalhador-jornada/analysis/${isAnalise}/update`,
        payload,
      );
    },
    exportHorarioEJornadasList: (filter) => {
      return axiosInstance.post(`${this.endpoint}/trabalhador-jornada/export`, filter, {
        responseType: 'blob',
      });
    },
    exportHistorico: (analysisId, filter) => {
      return axiosInstance.post(
        `${this.endpoint}/trabalhador-jornada/analysis/${analysisId}/history/export`,
        filter,
      );
    },
    exportMovimentacoes: (id, filter) => {
      return axiosInstance.post(
        `${this.endpoint}/trabalhador-jornada/${id}/movements/export`,
        filter,
        { responseType: 'blob' },
      );
    },
    filtrosMovimentacoes: (idTrabalhador) => {
      return axiosInstance.get(
        `${this.endpoint}/collaborator/${idTrabalhador}/horarios-jornada/events/filters`,
      );
    },
    fetchAllOverviewGraphs: (filter) => {
      return axiosInstance.post(`${this.endpoint}/trabalhador-jornada/analysis/dashboard`, filter);
    },
    listaPaginada: (filter, pagination) => {
      return axiosInstance.post(
        `${this.endpoint}/work-schedules/recommendations?${pagination}`,
        filter,
      );
    },
    getHorarioEJornadasHistory: (id) => {
      return axiosInstance.get(`${this.endpoint}/work-schedules/recommendations/${id}/history`);
    },

    risco: {
      fetchOneGraficoRisco: (filtros) => {
        return axiosInstance.post(`${this.endpoint}/trabalhador-jornada/grafico/risco`, filtros);
      },
      fetchAllRiscoTodos: (filtros, pagination) => {
        return axiosInstance.post(
          `${this.endpoint}/trabalhador-jornada/com-risco?${pagination}`,
          filtros,
        );
      },
      fetchAllRiscoTodosFiltros: () => {
        return axiosInstance.get(`${this.endpoint}/trabalhador-jornada/com-risco/filtros`);
      },
      exportRiscoTodos: (filtros) => {
        return axiosInstance.post(
          `${this.endpoint}/trabalhador-jornada/com-risco/export`,
          filtros,
          { responseType: 'blob' },
        );
      },
      fetchAllPorOcorrencias: (filtros, paginacao) => {
        return axiosInstance.post(
          `${this.endpoint}/trabalhador-jornada/com-risco/por-ocorrencia?${paginacao}`,
          filtros,
        );
      },
      exportPorOcorrencias: (filtros) => {
        return axiosInstance.post(
          `${this.endpoint}/trabalhador-jornada/com-risco/por-ocorrencia/export`,
          filtros,
          { responseType: 'blob' },
        );
      },
      fetchAllTrabalhadoresPorOcorrencia: (cnpj, ocorrenciaId) => {
        return axiosInstance.get(
          `${this.endpoint}/trabalhador-jornada/com-risco/por-ocorrencia/detalhe/${cnpj}/${ocorrenciaId}`,
        );
      },
      exportTrabalhadoresPorOcorrencia: (cnpj, ocorrenciaId) => {
        return axiosInstance.post(
          `${this.endpoint}/trabalhador-jornada/com-risco/por-ocorrencia/detalhe/export/${cnpj}/${ocorrenciaId}`,
        );
      },
    },
  };

  static configuracao = {
    fetchParametrosDeAnalise: () => {
      return axiosInstance.get(`${this.endpoint}/config-tables/analysis-parameter`);
    },

    getTabelas: () => {
      return axiosInstance.get(`${this.endpoint}/config-tables/tables`);
    },
  };

  static centralReprocessamento = {
    // HOME (CON-5281): uma linha por empregador — o último processamento de cada CNPJ.
    pesquisar: (pageable) => axiosInstance.get(`central-reprocessamento/empregadores?${pageable}`),
    // Histórico de um empregador: todos os processamentos do CNPJ (mais recente -> mais antigo).
    historico: (nrinsc, pageable) =>
      axiosInstance.get(`central-reprocessamento?nrinsc=${encodeURIComponent(nrinsc)}&${pageable}`),
    buscar: (id) => axiosInstance.get(`central-reprocessamento/${id}`),
    criar: (payload) => axiosInstance.post('central-reprocessamento', payload),
    resolicitar: (id, payload) =>
      axiosInstance.put(`central-reprocessamento/${id}/resolicitar`, payload),
  };
}
